import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { HeroProvider } from './contexts/HeroContext';
import { VoucherProvider } from './contexts/VoucherContext';
import { DeliveryLocationProvider } from './contexts/DeliveryLocationContext';
import { CartProvider } from './contexts/CartContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import { MaintenanceProvider } from './contexts/MaintenanceContext';
import ProtectedRoute from './components/ProtectedRoute';
import MaintenanceGuard from './components/MaintenanceGuard';
import UserOnlyRoute from './components/UserOnlyRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import TimeoutWarning from './components/TimeoutWarning';
import LoadingFallback from './components/LoadingFallback';
import ToastContainer from './components/ToastContainer';
import ChatWidget from './components/ChatWidget';
import AdminChatWidget from './components/AdminChatWidget';
import PageTransition from './components/PageTransition';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import EditProductPage from './pages/EditProductPage';
import ImageGalleryPage from './pages/ImageGalleryPage';
import './App.css';
import './styles/animations.css';

// Lazy load pages for code splitting
// Public/User Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ProductDetailsPage = lazy(() => import('./pages/ProductDetailsPage'));
const BundleDetailsPage = lazy(() => import('./pages/BundleDetailsPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const ReturnPolicyPage = lazy(() => import('./pages/ReturnPolicyPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

// Protected User Pages
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const PayMongoReturnPage = lazy(() => import('./pages/PayMongoReturnPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const CustomizePotPage = lazy(() => import('./pages/CustomizePotPage'));

// Admin Pages - Grouped for better code splitting
// This creates a separate admin bundle that only loads when admin routes are accessed
const AdminPages = lazy(() => import('./pages/admin'));

// Individual admin page imports (for better tree-shaking and smaller chunks)
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminProductsPage = lazy(() => import('./pages/AdminProductsPage'));
const AddProductPage = lazy(() => import('./pages/AddProductPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage'));
const AdminOrderReceiptPage = lazy(() => import('./pages/AdminOrderReceiptPage'));
const AdminBulkOrderReceiptsPage = lazy(() => import('./pages/AdminBulkOrderReceiptsPage'));
const PromotePage = lazy(() => import('./pages/PromotePage'));
const CreateAdminPage = lazy(() => import('./pages/CreateAdminPage'));
const VoucherPage = lazy(() => import('./pages/VoucherPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AdminCenterPage = lazy(() => import('./pages/AdminCenterPage'));
const AdminChatPage = lazy(() => import('./pages/AdminChatPage'));
const AdminNotificationsPage = lazy(() => import('./pages/AdminNotificationsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

function App() {
  return (
    <AuthProvider>
      <VoucherProvider>
        <NotificationProvider>
          <ChatProvider>
          <HeroProvider>
            <DeliveryLocationProvider>
            <CartProvider>
              <ToastProvider>
                <FavoritesProvider>
            <Router>
            <MaintenanceProvider>
            <ErrorBoundary>
            <MaintenanceGuard>
            <div className="App">
            <ScrollToTop />
            <ToastContainer />
            {/* Global Timeout Warning Component */}
            <TimeoutWarning />
            {/* Chat Widgets - User chat (bottom left) and Admin chat (bottom right) */}
            <ChatWidget />
            <AdminChatWidget />
            
            <Routes>
            <Route path="/" element={
              <Suspense fallback={<LoadingFallback message="Loading home page..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <LandingPage />
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/shop" element={
              <Suspense fallback={<LoadingFallback message="Loading shop..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ShopPage />
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/contacts" element={
              <Suspense fallback={<LoadingFallback message="Loading contacts..." />}>
                <PageTransition>
                  <ContactsPage />
                </PageTransition>
              </Suspense>
            } />
            <Route path="/about" element={
              <Suspense fallback={<LoadingFallback message="Loading about page..." />}>
                <PageTransition>
                  <AboutPage />
                </PageTransition>
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<LoadingFallback message="Loading terms..." />}>
                <TermsAndConditionsPage />
              </Suspense>
            } />
            <Route path="/return-policy" element={
              <Suspense fallback={<LoadingFallback message="Loading return policy..." />}>
                <ReturnPolicyPage />
              </Suspense>
            } />
            <Route path="/privacy-policy" element={
              <Suspense fallback={<LoadingFallback message="Loading privacy policy..." />}>
                <PrivacyPolicyPage />
              </Suspense>
            } />
            <Route path="/product/:productId" element={
              <Suspense fallback={<LoadingFallback message="Loading product..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ProductDetailsPage />
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/bundle/:bundleId" element={
              <Suspense fallback={<LoadingFallback message="Loading bundle..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <BundleDetailsPage />
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/cart" element={
              <Suspense fallback={<LoadingFallback message="Loading cart..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <CartPage />
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/login" element={
              <Suspense fallback={<LoadingFallback message="Loading login..." />}>
                <PageTransition>
                  <LoginPage />
                </PageTransition>
              </Suspense>
            } />
            <Route path="/register" element={
              <Suspense fallback={<LoadingFallback message="Loading registration..." />}>
                <PageTransition>
                  <RegisterPage />
                </PageTransition>
              </Suspense>
            } />
            <Route path="/forgot-password" element={
              <Suspense fallback={<LoadingFallback message="Loading..." />}>
                <PageTransition>
                  <ForgotPasswordPage />
                </PageTransition>
              </Suspense>
            } />
            <Route path="/reset-password" element={
              <Suspense fallback={<LoadingFallback message="Loading..." />}>
                <PageTransition>
                  <ResetPasswordPage />
                </PageTransition>
              </Suspense>
            } />
            <Route path="/verify-email" element={
              <Suspense fallback={<LoadingFallback message="Loading..." />}>
                <PageTransition>
                  <EmailVerificationPage />
                </PageTransition>
              </Suspense>
            } />
            <Route path="/profile" element={
              <Suspense fallback={<LoadingFallback message="Loading profile..." />}>
                <PageTransition>
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/notifications" element={
              <Suspense fallback={<LoadingFallback message="Loading notifications..." />}>
                <PageTransition>
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/checkout" element={
              <Suspense fallback={<LoadingFallback message="Loading checkout..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ProtectedRoute>
                      <CheckoutPage />
                    </ProtectedRoute>
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/payment" element={
              <Suspense fallback={<LoadingFallback message="Loading payment..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ProtectedRoute>
                      <PaymentPage />
                    </ProtectedRoute>
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/paymongo-return" element={
              <Suspense fallback={<LoadingFallback message="Finalizing your payment..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ProtectedRoute>
                      <PayMongoReturnPage />
                    </ProtectedRoute>
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/order-confirmation/:orderId" element={
              <Suspense fallback={<LoadingFallback message="Loading order confirmation..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ProtectedRoute>
                      <OrderConfirmationPage />
                    </ProtectedRoute>
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/orders" element={
              <Suspense fallback={<LoadingFallback message="Loading orders..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ProtectedRoute>
                      <OrdersPage />
                    </ProtectedRoute>
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/customize-pot" element={
              <Suspense fallback={<LoadingFallback message="Loading pot customizer..." />}>
                <PageTransition>
                  <UserOnlyRoute>
                    <ProtectedRoute>
                      <CustomizePotPage />
                    </ProtectedRoute>
                  </UserOnlyRoute>
                </PageTransition>
              </Suspense>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={
              <Suspense fallback={<LoadingFallback message="Loading admin login..." />}>
                <PageTransition>
                  <AdminLoginPage />
                </PageTransition>
              </Suspense>
            } />
            {/* Admin routes - all admins can access */}
            <Route path="/admin/products" element={
              <Suspense fallback={<LoadingFallback message="Loading products..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AdminProductsPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/image-gallery" element={
              <PageTransition>
                <ProtectedRoute requireAdmin={true}>
                  <ImageGalleryPage />
                </ProtectedRoute>
              </PageTransition>
            } />
            <Route path="/admin/products/add" element={
              <Suspense fallback={<LoadingFallback message="Loading add product..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AddProductPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/products/edit/:id" element={
              <PageTransition>
                <ProtectedRoute requireAdmin={true}>
                  <EditProductPage />
                </ProtectedRoute>
              </PageTransition>
            } />
            <Route path="/admin/customers" element={
              <Suspense fallback={<LoadingFallback message="Loading customers..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <CustomersPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/orders" element={
              <Suspense fallback={<LoadingFallback message="Loading admin orders..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AdminOrdersPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/orders/:orderId/receipt" element={
              <Suspense fallback={<LoadingFallback message="Loading order receipt..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AdminOrderReceiptPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/orders/receipts" element={
              <Suspense fallback={<LoadingFallback message="Loading receipts..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AdminBulkOrderReceiptsPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/promote" element={
              <Suspense fallback={<LoadingFallback message="Loading promotion page..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <PromotePage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            {/* Superadmin-only route for creating admins */}
            <Route path="/admin/create-admin" element={
              <Suspense fallback={<LoadingFallback message="Loading..." />}>
                <PageTransition>
                  <SuperAdminRoute>
                    <CreateAdminPage />
                  </SuperAdminRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/vouchers" element={
              <Suspense fallback={<LoadingFallback message="Loading vouchers..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <VoucherPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/inventory" element={
              <Suspense fallback={<LoadingFallback message="Loading inventory..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <InventoryPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/center" element={
              <Suspense fallback={<LoadingFallback message="Loading admin center..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AdminCenterPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/analytics" element={
              <Suspense fallback={<LoadingFallback message="Loading analytics..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AnalyticsPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            <Route path="/admin/notifications" element={
              <Suspense fallback={<LoadingFallback message="Loading notifications..." />}>
                <PageTransition>
                  <ProtectedRoute requireAdmin={true}>
                    <AdminNotificationsPage />
                  </ProtectedRoute>
                </PageTransition>
              </Suspense>
            } />
            
            {/* Catch-all route for debugging */}
            <Route path="*" element={
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
                <p>Current path: {window.location.pathname}</p>
                <p>Available routes include: /, /shop, /contacts, /cart, /login, /register</p>
              </div>
            } />
            </Routes>
              </div>
            </MaintenanceGuard>
            </ErrorBoundary>
            </MaintenanceProvider>
            </Router>
                </FavoritesProvider>
              </ToastProvider>
            </CartProvider>
            </DeliveryLocationProvider>
          </HeroProvider>
          </ChatProvider>
        </NotificationProvider>
      </VoucherProvider>
    </AuthProvider>
  );
}

export default App;
